import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { Box, ButtonBase, Stack, Typography } from '@mui/material';
import React from 'react';
import { useHref, useNavigate } from 'react-router';
import { themeColors } from '../JuvareTheme';
import { NavigationContext } from '../NavigationContext';
import { colors } from './colors';
/**
 * Shows a number in a card. When clicked can navigate somewhere.
 *
 * @param {object} root0 - Component props.
 * @param {React.ReactNode} root0.icon - The icon to display next to the heading.
 * @param {string} root0.headingText - The main heading text shown in the card.
 * @param {'neutral' | keyof typeof colors} root0.headingColor - The color theme for the heading (e.g., 'primary', 'neutral').
 * @param {string | number} root0.content - The main numerical/statistical content.
 * @param {string} [root0.href] - Optional external URL to navigate to.
 * @param {string | number | { to: string | number; options?: NavigateOptions }} [root0.navigate] - Internal navigation configuration.
 * @param {() => void} [root0.onClick] - Optional click handler callback.
 * @param {string} [root0.footerText] - Optional footer text with a warning icon.
 * @param {string} [root0.'data-test'] - Optional test identifier.
 * @returns {JSX.Element} Rendered StatisticsCard component.
 */
export const StatisticsCard = ({ icon, headingText, headingColor, content, href, navigate, onClick, footerText, 'data-test': dataTest, }) => {
    const navigationContext = React.useContext(NavigationContext);
    const navigateTo = typeof navigate === 'object' ? navigate === null || navigate === void 0 ? void 0 : navigate.to : navigate === null || navigate === void 0 ? void 0 : navigate.toString();
    const navigateHref = useHref(navigateTo !== null && navigateTo !== void 0 ? navigateTo : '');
    const navigateFn = useNavigate();
    const handleNavigate = () => {
        const options = {};
        if (navigate) {
            if (typeof navigate === 'object') {
                Object.assign(options, navigate.options);
            }
        }
        navigationContext.navigate(() => navigateFn(navigateHref, options));
    };
    return (React.createElement(ButtonBase, { component: 'a', href: href !== null && href !== void 0 ? href : navigateHref, onClick: (event) => {
            if (onClick) {
                event.preventDefault();
                navigationContext.navigate(onClick);
            }
            else if (navigateTo) {
                event.preventDefault();
                handleNavigate();
            }
        }, focusRipple: true, sx: {
            height: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            backgroundColor: themeColors.surface.secondary,
            cursor: 'pointer',
            borderRadius: 1,
            '&:hover': {
                backgroundColor: themeColors.surface.secondary,
            },
            '&:hover::after': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: themeColors.surface.hover,
            },
            p: 2,
        }, "data-test": dataTest !== null && dataTest !== void 0 ? dataTest : 'statistics-card' },
        React.createElement(Stack, { gap: 2 },
            React.createElement(Stack, { gap: 1, direction: 'row', sx: {
                    alignItems: 'center',
                    color: headingColor === 'neutral' ? colors[headingColor].icon : colors[headingColor],
                }, "data-test": 'heading-content' },
                icon,
                React.createElement(Typography, { variant: 'subtitle1', sx: { color: headingColor === 'neutral' ? colors[headingColor].text : 'inherit' } }, headingText)),
            React.createElement(Typography, { component: 'p', variant: 'h1', sx: { color: themeColors.text.main } }, content),
            footerText && (React.createElement(Stack, { direction: 'row', gap: 1, alignItems: 'center', "data-test": 'footer-content' },
                React.createElement(ReportProblemIcon, { color: 'warning', fontSize: 'medium' }),
                React.createElement(Typography, { variant: 'body2', color: colors.deepOrange }, footerText)))),
        React.createElement(Box, { sx: { display: 'flex', marginLeft: 'auto' } },
            React.createElement(ChevronRightIcon, { sx: { color: themeColors.text.main }, fontSize: 'medium' }))));
};
